import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Headline, Paragraph, Title, Subheader, Dropdown, TextField, Button } from '@cambridgeassessment/cambridge-ui';
import { RootState } from '../../../store';
import { Series } from '../../../types/Series';
import thunks from '../../../store/series/thunks';
import { getComponentsWithSubmittedSections } from '../../../store/replacementFiles/thunks';
import * as actions from '../../../store/replacementFiles/actions';
import { updateItemInStorage } from '../../../util/sessionStorage';
import {
  StyledInputContainer,
  ExpandCardTitle,
  StyledContainer,
  StyledInputFlexContainer,
  StyledResultsContainer,
} from './ReplacementFiles.styles';
import Loader from '../../../components/Loader/Loader';
import FileAssociationModal from './components/FileAssociationModal/FileAssociationModal';
import ComponentsList from './components/ComponentsList/ComponentsList';
import { Roles } from '@cambridgeassessment/lib-ee-commons';
import { Authorized } from 'lib-browser';

interface SearchTerm {
  centreNumber: string | null;
  candidateNumber: string | null;
  syllabusNumber: string | null;
  componentNumber: string | null;
}

const ReplacementFiles = (): JSX.Element => {
  const dispatch = useDispatch();
  const {
    series: { isLoading, seriesData, hasError },
    replacementFiles: {
      items: components,
      linkedCandidateModal: { isShown, selectedLearner },
    },
    userData: { businessUnit, selectedRole },
  } = useSelector((state: RootState) => state);

  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null);
  const [centreNumber, setCentreNumber] = useState<string | null>(sessionStorage.getItem('centre'));
  const [candidateNumber, setCandidateNumber] = useState<string | null>(sessionStorage.getItem('candidate'));
  const [syllabusNumber, setSyllabusNumber] = useState<string | null>(sessionStorage.getItem('syllabus'));
  const [componentNumber, setComponentNumber] = useState<string | null>(sessionStorage.getItem('component'));
  const [searchTerm, setSearchTerm] = useState<SearchTerm | null>(null);

  const updateSearchParameters = () => {
    const newParameters = {
      centreNumber,
      candidateNumber: candidateNumber ?? '',
      syllabusNumber: syllabusNumber ?? '',
      componentNumber: componentNumber ?? '',
    };

    setSearchTerm(newParameters);
  };

  const createSearchTerm = (params: SearchTerm | null) => {
    if (!params) {
      return null;
    }
    let searchParam = `'${params.centreNumber}'`;

    if (params.candidateNumber) {
      searchParam += ` & '${params.candidateNumber}'`;
    }
    if (params.syllabusNumber) {
      searchParam += ` & '${params.syllabusNumber}'`;
    }
    if (params.componentNumber) {
      searchParam += ` & '${params.componentNumber}'`;
    }

    return searchParam;
  };

  const setAndFetchSeriesDetails = (series: Series): void => {
    setSelectedSeries(series);
    if (businessUnit) dispatch(thunks.getDetailedSeries(businessUnit.id, series.id));
  };

  useEffect(() => {
    if (businessUnit) dispatch(thunks.getSeriesData(businessUnit.id));

    return (): void => {
      dispatch(actions.resetEntriesList());
    };
  }, [businessUnit, dispatch]);

  useEffect(() => {
    if (seriesData && seriesData[0] && businessUnit) {
      const storedSelectedSeries = sessionStorage.getItem('selectedSeries');
      const preselectedSeries = storedSelectedSeries && seriesData.find((item) => item.name === storedSelectedSeries);
      const series = preselectedSeries || seriesData[0];
      setSelectedSeries(series);
      dispatch(thunks.getDetailedSeries(businessUnit.id, series.id));
    }
  }, [businessUnit, seriesData, dispatch]);

  const onSeriesSelected = (series: Series): void => {
    sessionStorage.setItem('selectedSeries', series.name);
    setAndFetchSeriesDetails(series);
    dispatch(actions.resetEntriesList());
    updateSearchParameters();
  };

  const cacheCandidate = (): void => {
    updateItemInStorage('candidate', candidateNumber);
  };

  const cacheCentre = (): void => {
    updateItemInStorage('centre', centreNumber);
  };

  const cacheSyllabus = (): void => {
    updateItemInStorage('syllabus', syllabusNumber);
  };

  const cacheComponent = (): void => {
    updateItemInStorage('component', componentNumber);
  };

  const renderSeries = (item: Series): string => item.name;

  const onCentreChange = (value: string): void => setCentreNumber(value);

  const onCandidateChange = (value: string): void => setCandidateNumber(value);

  const onSyllabusChange = (value: string): void => setSyllabusNumber(value);

  const onComponentChange = (value: string): void => setComponentNumber(value);

  const fetchComponents = (searchParameters: SearchTerm): void => {
    if (!centreNumber || !selectedSeries) return;
    dispatch(
      getComponentsWithSubmittedSections({
        businessUnitId: businessUnit?.id as string,
        seriesId: selectedSeries?.id,
        centreId: searchParameters.centreNumber as string,
        learnerNumber: searchParameters.candidateNumber,
        assessmentCode: searchParameters.syllabusNumber,
        componentNumber: searchParameters.componentNumber,
      }),
    );
  };

  const onCandidateSearch = (): void => {
    dispatch(actions.resetEntriesList());
    updateSearchParameters();
  };

  useEffect(() => {
    if (!searchTerm) return;
    fetchComponents(searchTerm);
  }, [searchTerm]);

  const renderResultsTitle = (): JSX.Element => {
    if (!components?.length) {
      return <Title data-qa-id='search-result-message'>No results found for {createSearchTerm(searchTerm)}</Title>;
    }

    return (
      <>
        <Title data-qa-id='search-result-message'>Results for {createSearchTerm(searchTerm)}</Title>
        <ExpandCardTitle>Click on each card to expand it</ExpandCardTitle>
      </>
    );
  };

  const renderComponentsSearch = (): JSX.Element | undefined => {
    if (selectedSeries == null) {
      return undefined;
    }

    return (
      <StyledInputFlexContainer>
        <Title>Search for components</Title>
        <StyledInputContainer>
          <Subheader>Syllabus</Subheader>
          <TextField
            dataQaId='search-syllabus'
            onChange={onSyllabusChange}
            placeholder='Syllabus number'
            helperText='Optional'
            onBlur={cacheSyllabus}
            value={syllabusNumber || ''}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <Subheader>Component</Subheader>
          <TextField
            dataQaId='search-component'
            onChange={onComponentChange}
            placeholder='Component number'
            helperText='Optional'
            onBlur={cacheComponent}
            value={componentNumber || ''}
          />
        </StyledInputContainer>
      </StyledInputFlexContainer>
    );
  };

  const renderCandidateSearch = (): JSX.Element | undefined => {
    if (selectedSeries == null) {
      return undefined;
    }

    return (
      <StyledInputFlexContainer>
        <Title>Search for one or more candidates</Title>
        <StyledInputContainer>
          <Subheader>Centre number</Subheader>
          <TextField
            dataQaId='search-centre'
            onChange={onCentreChange}
            placeholder='Centre number'
            onBlur={cacheCentre}
            value={centreNumber || ''}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <Subheader>Candidate number</Subheader>
          <TextField
            dataQaId='search-candidate'
            onChange={onCandidateChange}
            placeholder='Candidate number'
            helperText='Optional'
            onBlur={cacheCandidate}
            value={candidateNumber || ''}
          />
        </StyledInputContainer>
        <Button onClick={onCandidateSearch} disabled={!centreNumber} qaId='search-button'>
          Search
        </Button>
        <StyledResultsContainer>
          {searchTerm && renderResultsTitle()}
          <ComponentsList
            businessUnitId={businessUnit?.id}
            seriesId={selectedSeries?.id}
            centreId={searchTerm?.centreNumber}
            learnerNumber={searchTerm?.candidateNumber}
          />
        </StyledResultsContainer>
      </StyledInputFlexContainer>
    );
  };

  if (isLoading) return <Loader />;

  if (hasError) return <div data-qa-id='replacement-files-error'>Failed to get Series data</div>;

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE]}
    >
      <StyledContainer data-qa-id='page-priming-replacement-files'>
        <Headline data-qa-id='replacement-files-header'>Replacement files</Headline>
        <Paragraph>
          Please select candidates within a series from a given centre to allow replacement files to be uploaded.
        </Paragraph>
        <Title>Select series</Title>
        {selectedSeries && (
          <StyledInputContainer>
            <Dropdown<Series>
              data={seriesData as Series[]}
              renderOption={renderSeries}
              renderSelectedOption={renderSeries}
              dataQaId='series-select'
              onChange={onSeriesSelected}
              value={selectedSeries}
              placeholder={'Series'}
              title='Select series'
              helperText='Choose either the current series or the most recently completed series'
            />
          </StyledInputContainer>
        )}

        {renderComponentsSearch()}
        {renderCandidateSearch()}
      </StyledContainer>
      <FileAssociationModal isShown={isShown} selectedLearner={selectedLearner} />
    </Authorized>
  );
};

export default ReplacementFiles;
